import { useEffect, useState } from 'react';
import { HiCheckCircle, HiOutlineMail, HiExclamationCircle } from 'react-icons/hi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { resetCheckoutState } from '../store/checkoutSlice';
import { removeItemsById, clearCart, CartItem } from '../store/cartSlice';
import { FaEnvelope, FaDownload } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

// Interface pour typer la session Stripe récupérée (partielle)
interface StripeSessionLineItem {
    id: string;
    price?: {
        product?: {
            id: string;
            name?: string;
            metadata?: {
                cartItemId?: string;
            };
        };
    };
    quantity?: number;
    // ... autres propriétés de line item ...
}

interface StripeSession {
    id: string;
    payment_status: string;
    line_items?: {
        data: StripeSessionLineItem[];
    };
    // ... autres propriétés de session ...
}

interface VerifyPaymentResponse {
    isPaid: boolean;
    session?: StripeSession;
    error?: string;
    payment_status?: string;
}

// Nouvel état simplifié pour les items payés
interface PaidItemInfo {
    cartItemId: string;
    name?: string; // Nom du produit si disponible
}

// Ajouter PAPER_SIZES ici ou l'importer
// Idéalement, partager cette définition entre frontend et backend
const PAPER_SIZES = [
  { id: 'A4', name: 'A4', dimensions: '21 x 29.7 cm', renderWidth: 850.32, renderHeight: 1202.48 },
  { id: 'A3', name: 'A3', dimensions: '29.7 x 42 cm', renderWidth: 1202.48, renderHeight: 1697.84 },
  { id: '70x100', name: '70x100 cm', dimensions: '70 x 100 cm', renderWidth: 2645.67, renderHeight: 3779.53 }, // Exemple
  // ... autres tailles
];

const backendUrl = import.meta.env.VITE_BACKEND_URL;

const Success = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  // Supprimé: cartItemsFromRedux (plus besoin ici)
  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'loading' | 'paid' | 'unpaid' | 'error'>('loading');
  // État simplifié : juste la liste des infos des items payés
  const [paidItems, setPaidItems] = useState<PaidItemInfo[]>([]); 
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const { t } = useTranslation();

  // Extraire session_id de l'URL au montage
  useEffect(() => {
      setSessionId(new URLSearchParams(location.search).get('session_id'));
  }, [location.search]);

  // Vérifier le statut du paiement quand sessionId est disponible
  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
          setIsLoading(false);
          setPaymentStatus('error');
          setErrorMessage('ID de session manquant.');
          return;
      }
      
      setIsLoading(true);
      setPaymentStatus('loading');
      setErrorMessage(null);
      setPaidItems([]); // Réinitialiser les items payés

      try {
        console.log(`Vérification du paiement pour la session: ${sessionId}`);
        const response = await fetch(`${backendUrl}/api/verify-payment/${sessionId}`);
        const data: VerifyPaymentResponse = await response.json();

        if (!response.ok || !data.isPaid || !data.session) {
          console.error('Échec de la vérification ou paiement non confirmé:', data);
          setPaymentStatus('unpaid');
          setErrorMessage(data.error || `Statut de paiement: ${data.payment_status || 'Inconnu'}`);
          throw new Error(data.error || 'Paiement non vérifié ou session invalide.');
        }
        
        console.log('Paiement vérifié avec succès. Session:', data.session);
        setPaymentStatus('paid');

        // Extraire les infos des line_items payés
        const itemsInfo = data.session.line_items?.data
          .map(item => ({
            cartItemId: item.price?.product?.metadata?.cartItemId,
            name: item.price?.product?.name
          }))
          .filter(info => typeof info.cartItemId === 'string')
          .map(info => info as PaidItemInfo) ?? []; 
        
        if (itemsInfo.length === 0) {
            console.warn("Aucun cartItemId trouvé dans les métadonnées de la session payée.", data.session);
            setPaymentStatus('error');
            setErrorMessage("Impossible de récupérer les détails de la commande.");
        } else {
            console.log("Infos des articles payés:", itemsInfo);
            
            // <-- MODIFICATION: Vider le panier Redux MAINTENANT -->
            const paidCartItemIds = itemsInfo.map(item => item.cartItemId);
            if (paidCartItemIds.length > 0) {
                console.log("Suppression des articles payés du panier Redux:", paidCartItemIds);
                dispatch(removeItemsById(paidCartItemIds));
            }
            // <-- FIN MODIFICATION -->

            // Mettre à jour l'état local pour l'affichage des boutons
            setPaidItems(itemsInfo);
        }

      } catch (error) {
        console.error('Erreur lors de la vérification du paiement:', error);
        if (paymentStatus !== 'unpaid') {
            setPaymentStatus('error');
            const specificErrorMessage = error instanceof Error ? error.message : 'Une erreur est survenue lors de la vérification.';
            setErrorMessage(specificErrorMessage);
        }
      } finally {
        setIsLoading(false);
      }
    };

    verifyPayment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, dispatch]); 

  // Après succès paiement, vider le panier Redux côté frontend (Success.tsx)
  useEffect(() => {
    if (paymentStatus === 'paid') {
      dispatch(clearCart()); // Action Redux à créer/importer si non existante
    }
  }, [paymentStatus, dispatch]);

  // --- Fonction de Téléchargement (simplifiée) --- 
  const handleDownload = (cartItemId: string) => {
    if (paymentStatus !== 'paid') {
      alert('Le paiement doit être validé avant de télécharger le PDF.');
      return;
    }
    console.log(`Téléchargement demandé pour ${cartItemId}`);
    window.open(`${backendUrl}/api/download-pdf/${cartItemId}`, '_blank');
    console.log(`Ouverture de l'URL de téléchargement lancée pour ${cartItemId}`);
  };

  // ----- Rendu conditionnel (adapté) ----- 
  if (isLoading || paymentStatus === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] text-center p-6 text-white">
        <div className="max-w-xl w-full bg-[#2a2a2a] p-8 rounded-lg shadow-xl">
          <span className="text-lg font-semibold">{t('success.loading', 'Vérification du paiement en cours...')}</span>
        </div>
      </div>
    );
  }
  if (paymentStatus === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] text-center p-6 text-white">
        <div className="max-w-xl w-full bg-[#2a2a2a] p-8 rounded-lg shadow-xl">
          <span className="text-lg font-semibold text-red-500">{t('success.error', 'Erreur lors de la vérification du paiement.')}</span>
          {errorMessage && <div className="mt-2 text-sm text-gray-400">{errorMessage}</div>}
        </div>
      </div>
    );
  }
  if (paymentStatus === 'unpaid') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] text-center p-6 text-white">
        <div className="max-w-xl w-full bg-[#2a2a2a] p-8 rounded-lg shadow-xl">
          <span className="text-lg font-semibold text-yellow-400">{t('success.unpaid', 'Le paiement n\'a pas été validé. Merci de réessayer ou de contacter le support.')}</span>
        </div>
      </div>
    );
  }
  // Si paymentStatus === 'paid'
  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] text-center px-1 py-6 text-white">
      <div className="max-w-xl w-full bg-[#2a2a2a] p-8 rounded-lg shadow-xl">
        <HiCheckCircle className="w-20 h-20 text-green-500 mx-auto mb-5" />
        <h2 className="text-base font-bold mb-2">{t('success.title', 'Paiement validé !')}</h2>
        <p className="mb-4 text-sm text-gray-300">{t('success.subtitle', 'Merci pour votre achat. Vous pouvez télécharger vos affiches ci-dessous.')}</p>
        <ul className="mb-6">
          {paidItems.map(item => (
            <li key={item.cartItemId} className="flex flex-col items-center justify-between space-y-3 bg-[#222] rounded px-4 py-4 mb-2">
              <span className='text-xs font-semibold'>{item.name || t('cart.defaultTitle', 'Affiche personnalisée')}</span>
              <span className='w-full border-t border-[#333]'></span>
              <button
                className={"ml-4 px-4 py-1 rounded cursor-pointer bg-green-600 hover:bg-green-700 text-white text-sm"}
                onClick={() => handleDownload(item.cartItemId)}
              >
                {t('success.download', 'Télécharger le PDF')}
              </button>
            </li>
          ))}
        </ul>
        <button
          className="mt-2 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded cursor-pointer"
          onClick={() => navigate('/')}
        >
          {t('success.backToEditor', 'Retour à l\'éditeur')}
        </button>
      </div>
    </div>
  );
};

export default Success;